import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase/client';
import { toast } from 'sonner';
import type { Profile } from '../../types/profile';
import { useSupabaseAuth } from '../auth/useSupabaseAuth';

interface BusinessProfile extends Profile {
  business_id: number;
  is_imported?: boolean;
  data_source?: string;
  google_place_id?: string;
}

export function useActiveBusinessProfile() {
  const [profile, setProfile] = useState<BusinessProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { user, isSignedIn, isLoaded } = useSupabaseAuth();

  const loadActiveBusinessProfile = async () => {
    if (!isSignedIn || !user) {
      setProfile(null);
      setLoading(false);
      return;
    }

    try {
      // Query the business where auth_user_id matches the current auth user
      const { data: businessData, error: businessError } = await supabase
        .from('users')
        .select(`
          *,
          user_metadata!user_metadata_user_id_fkey(
            website,
            address_line1,
            address_line2,
            city,
            county,
            country,
            import_batch_id,
            data_source,
            google_place_id
          )
        `)
        .eq('auth_user_id', user.id)
        .single();

      if (businessError) {
        // PGRST116 = "no rows returned" - user has no claimed business
        if (businessError.code === 'PGRST116') {
          setProfile(null);
          setError(null);
          setLoading(false);
          return;
        }
        throw businessError;
      }

      if (!businessData) {
        setProfile(null);
        setError(null);
        setLoading(false);
        return;
      }

      // Convert to BusinessProfile format
      const userMetadata = Array.isArray(businessData.user_metadata)
        ? businessData.user_metadata[0] || {}
        : businessData.user_metadata || {};

      const businessProfile: BusinessProfile = {
        id: businessData.user_id || user.id,
        business_id: businessData.id,
        company_name: businessData.company_name,
        telephone_number: businessData.phone || null,
        website: businessData.website || userMetadata.website || null,
        address_line1: businessData.address_line1 || userMetadata.address_line1 || null,
        address_line2: businessData.address_line2 || userMetadata.address_line2 || null,
        city: businessData.city || userMetadata.city || null,
        county: businessData.county || userMetadata.county || null,
        postcode: businessData.company_postcode || null,
        country: businessData.country || userMetadata.country || 'United Kingdom',
        service_radius: businessData.service_radius || 10,
        share_location: businessData.share_location || false,
        latitude: businessData.latitude || null,
        longitude: businessData.longitude || null,
        created_at: businessData.created_at,
        updated_at: businessData.created_at,
        is_imported: !!userMetadata.import_batch_id || userMetadata.data_source === 'google_places',
        data_source: userMetadata.data_source || null,
        google_place_id: userMetadata.google_place_id || null
      };

      setProfile(businessProfile);
      setError(null);
    } catch (err) {
      console.error('Active business profile loading error:', err);
      setError(err instanceof Error ? err : new Error('Failed to load business profile'));
      toast.error('Failed to load business profile');
    } finally {
      setLoading(false);
    }
  };

  const updateBusinessProfile = async (businessId: number, updates: Partial<Profile>) => {
    if (!isSignedIn || !user) {
      throw new Error('Not authenticated');
    }

    try {
      const userUpdates: Record<string, any> = {};

      if (updates.company_name !== undefined) userUpdates.company_name = updates.company_name;
      if (updates.telephone_number !== undefined) userUpdates.phone = updates.telephone_number;
      if (updates.postcode !== undefined) userUpdates.company_postcode = updates.postcode;
      if (updates.share_location !== undefined) userUpdates.share_location = updates.share_location;
      if (updates.latitude !== undefined) userUpdates.latitude = updates.latitude;
      if (updates.longitude !== undefined) userUpdates.longitude = updates.longitude;
      if (updates.address_line1 !== undefined) userUpdates.address_line1 = updates.address_line1;
      if (updates.address_line2 !== undefined) userUpdates.address_line2 = updates.address_line2;
      if (updates.city !== undefined) userUpdates.city = updates.city;
      if (updates.county !== undefined) userUpdates.county = updates.county;
      if (updates.country !== undefined) userUpdates.country = updates.country;
      if (updates.website !== undefined) userUpdates.website = updates.website;

      const { error: updateError } = await supabase
        .from('users')
        .update(userUpdates)
        .eq('id', businessId)
        .eq('auth_user_id', user.id);

      if (updateError) throw updateError;

      await loadActiveBusinessProfile();
      toast.success('Business profile updated successfully');
      return profile;
    } catch (err) {
      console.error('Business profile update error:', err);
      const message = err instanceof Error ? err.message : 'Failed to update business profile';
      setError(new Error(message));
      toast.error(message);
      throw err;
    }
  };

  const unclaimBusiness = async (businessId: number) => {
    if (!user) return false;

    try {
      // Remove auth_user_id link (unclaim the business)
      const { error: updateError } = await supabase
        .from('users')
        .update({ auth_user_id: null })
        .eq('id', businessId)
        .eq('auth_user_id', user.id);

      if (updateError) throw updateError;

      // Update user_metadata to mark as unclaimed
      const { data: businessData } = await supabase
        .from('users')
        .select('user_id')
        .eq('id', businessId)
        .single();

      if (businessData?.user_id) {
        await supabase
          .from('user_metadata')
          .update({
            profile_claimed: false,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', businessData.user_id);
      }

      // Reload profile (will be null now)
      await loadActiveBusinessProfile();
      return true;
    } catch (err) {
      console.error('Error unclaiming business:', err);
      setError(err instanceof Error ? err : new Error('Failed to unclaim business'));
      return false;
    }
  };

  useEffect(() => {
    if (isLoaded) {
      loadActiveBusinessProfile();
    }
  }, [user, isSignedIn, isLoaded]);

  return {
    profile,
    loading,
    error,
    updateProfile: profile ? (updates: Partial<Profile>) => updateBusinessProfile(profile.business_id, updates) : undefined,
    unclaimBusiness,
    reloadProfile: loadActiveBusinessProfile,
    isInitialized: !loading
  };
}
